import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { RouteConfig } from 'src/types/route-config.types';
import { QueryState } from "src/types/query-state";
import qs from "qs";
import axios, { AxiosError, AxiosResponse } from "axios";
import { createContext } from "react";
import { QueryResponseContextProps } from "src/types/query-state.type";
import { uploadFileToServer } from "./api/public/public.api";
import { ApiError } from "./api/ApiError";
import { UseFormReturn } from "react-hook-form";
import { ZodObject, ZodType } from "zod";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getDefaultRouteForRole = (
  role: string,
  routes: RouteConfig[]
): string => {
  const defaultRoute = routes.find(route =>
    route?.isDefaultFor?.includes(role)
  );

  return defaultRoute?.path || '/';
};

export const getDefaultRoute = (
  routes: RouteConfig[]
): string => {
  const defaultRoute = routes.find(route =>
    route?.isDefaultFor && route?.isDefaultFor?.includes("*")
  );

  return defaultRoute?.path || '/auth/login';
};

// Utility to check if API response was successful
export const IS_SUCCESS_RESPONSE = (resp: any): boolean => {
  return resp && (resp.code === 200 || resp.status === 200 || resp.success === true);
};

export const startCase = (string: any) => {
  return string
    ?.replace(/[\W_]+/g, " ")
    .replace(/(?<=[a-z])([A-Z])/g, " $1")
    .replace(/^[a-z]/, (match: any) => match.toUpperCase());
};

export const camelCase = (string: any) => {
  return string?.replace(/[\W_]+(.|$)/g, (_: any, chr: any) => " " + chr.toUpperCase()).trim();
};

export function convertToCamelCase(str: any) {
  return startCase(camelCase(str));
}


export function getInitials(name: string) {
  if (!name || typeof name !== 'string') {
    return "";
  }
  const words = name.split(' ').filter(word => word.length > 0);
  if (words.length === 0) {
    return "";
  }
  const firstLetters = words.map(word => word[0]);
  return firstLetters.join('').toUpperCase();
}

function isNotEmpty(obj: unknown) {
  return obj !== undefined && obj !== null && obj !== "";
}


// src/lib/utils.ts (or wherever stringifyRequestQuery is defined)

/**
 * Convert query state to URL query string
 * Skips: undefined, null, empty string, "all"
 */
// src/lib/utils.ts

/**
 * Convert query state to URL query string (WITHOUT leading ?)
 * Caller is responsible for adding ?
 */
export const stringifyRequestQuery = (state: any): string => {
  if (!state) return '';

  const params: string[] = [];

  // ── Date range ───────────────────────────────────────────────
  if (state.filter) {
    const { from, to } = state.filter;
    if (from) params.push(`from=${from}`);
    if (to)   params.push(`to=${to}`);
  }

  // ── f filters ────────────────────────────────────────────────
  if (state.f) {
    const fFilters: string[] = [];

    Object.entries(state.f).forEach(([key, value]) => {
      const stringValue = String(value ?? '').trim().toLowerCase();

      if (
        value === undefined ||
        value === null ||
        stringValue === '' ||
        stringValue === 'all'
      ) {
        return;
      }

      fFilters.push(`${key}:${value}`);
    });

    if (fFilters.length > 0) {
      params.push(`f=${fFilters.join(';')}`);
    }
  }

  // ✅ Return WITHOUT leading ?
  return params.join('&');
};

export function parseRequestQuery(query: string): QueryState | any {
  const cache: unknown = qs.parse(query);
  return cache as QueryState | any;
}

// src/lib/utils.ts

/**
 * Convert flat object to query string
 * Skips: undefined, null, empty string, "all"
 */
// src/lib/utils.ts

/**
 * Convert flat object to query string (WITHOUT leading ?)
 */
export const stringifyObjectToRequestQuery = (
  obj: Record<string, any>
): string => {
  if (!obj || typeof obj !== 'object') return '';

  const fFilters: string[] = [];

  Object.entries(obj).forEach(([key, value]) => {
    const stringValue = String(value ?? '').trim().toLowerCase();

    if (
      value === undefined ||
      value === null ||
      stringValue === '' ||
      stringValue === 'all'
    ) {
      return;
    }

    fFilters.push(`${key}:${value}`);
  });

  if (fFilters.length === 0) return '';

  // ✅ Return WITHOUT leading ?
  return `f=${fFilters.join(';')}`;
};


export function formatIndianAmount(value: string | number, decimalPlaces: number = 0): string {
  if (!value) return '';

  let numStr = value.toString().replace(/,/g, ''); // Remove commas to handle formatted inputs
  const num = parseFloat(numStr);
  if (isNaN(num)) return '';

  // Round to the specified decimal places
  const multiplier = Math.pow(10, decimalPlaces);
  const roundedNum = Math.abs(num * multiplier) / multiplier;

  // Format using Indian locale with specified decimal places
  const formatted = Number(roundedNum).toLocaleString('en-IN', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  });

  return formatted;
}


export function parseIndianAmount(formatted: string): number {
  const numStr = formatted.replace(/,/g, '');
  return parseInt(numStr, 10) || 0;
}



export const catchAsync = (asyncFunction: any, onSuccess: any, onError?: any, callBackFunction?: (e: any) => void) => {
  return async (...args: any[]) => {
    try {
      const result = await asyncFunction(...args);
      if (onSuccess) {
        onSuccess(result, ...args);
      }
      callBackFunction && callBackFunction(result);
    } catch (error) {
      onError && onError();
      console.log("error oin catch");


      let err = error as Error | AxiosError;
      if (axios.isAxiosError(err)) {
        if (err) {
          const status = err.response?.status;
          // toast({
          //   variant: "destructive",
          //   title: err.response?.data?.message || "Error",
          //   description: "",
          // });
          // }
        }
      } else {
        // toast({
        //   variant: "destructive",
        //   title: "Unexpected error",
        //   description: err?.message,
        // });
      }
    }
  };
};

export const checkAnyFileToBeUpload = async (
  obj: any,
  documentType: string | any
): Promise<any> => {
  const uploadedFiles: any = {};
  let currentFile = "";

  // ✅ Helper to check if object is a document object { file, type }
  const isDocumentObject = (d: any): boolean => {
    return (
      typeof d === "object" &&
      d !== null &&
      "file" in d &&
      "type" in d
    );
  };

  try {
    for (const [key, value] of Object.entries(obj)) {
      currentFile = key;

      if (Array.isArray(value)) {

        // ✅ Check if it's a document array [{ file, type }, ...]
        const isDocumentArray = value.length > 0 && value.every(isDocumentObject);

        if (isDocumentArray) {
          // Document array - upload files
          const extract_files_only = value.filter((d: any) => d.file instanceof File);
          const extract_previously_uploaded_only = value.filter((d: any) => typeof d.file === "string");

          // Skip empty file objects like { file: {}, type: "..." }
          // const emptyFiles = value.filter((d: any) => 
          //     typeof d.file === "object" && 
          //     !(d.file instanceof File) && 
          //     Object.keys(d.file).length === 0
          // );

          if (extract_files_only.length > 0) {
            // Upload new files
            const prepareNewArray = await Promise.all(
              extract_files_only.map((d) => uploadFileToServer(d.file, d.type))
            );

            // Check for any failed upload
            const failed = prepareNewArray.find((res) => res.code !== 200);
            if (failed) {
              return [obj, false, {
                dataKey: currentFile,
                message: convertToCamelCase(currentFile) + " : " + (failed.message || "Upload failed"),
                status: failed.code
              }];
            }

            // Remove code from each upload response
            uploadedFiles[key] = [
              ...extract_previously_uploaded_only,
              ...prepareNewArray.map(({ code, ...rest }) => rest)
            ];
          } else {
            // No new files to upload, keep previously uploaded only
            uploadedFiles[key] = extract_previously_uploaded_only;
          }
        }
        // ✅ Array of direct File objects
        else if (value.every((item) => item instanceof File)) {
          const filesToUpload = value;

          const newFiles = await Promise.all(
            filesToUpload.map((file) =>
              uploadFileToServer(
                file,
                typeof documentType === "object" ? documentType[key] : documentType
              )
            )
          );

          // Check for any failed upload
          const failed = newFiles.find((res: any) => res.code !== 200);
          if (failed) {
            return [obj, false, {
              dataKey: currentFile,
              message: convertToCamelCase(currentFile) + " : " + (failed.message || "Upload failed"),
              status: failed.code
            }];
          }

          uploadedFiles[key] = newFiles.map(({ code, ...rest }: any) => rest);
        }
        // ✅ Mixed array (Files + strings)
        else if (value.some((item) => item instanceof File)) {
          const filesToUpload = value.filter((item) => item instanceof File);
          const filesPreviouslyUploaded = value.filter((item) => typeof item === "string");

          const newFiles = await Promise.all(
            filesToUpload.map((file) =>
              uploadFileToServer(
                file,
                typeof documentType === "object" ? documentType[key] : documentType
              )
            )
          );

          // Check for any failed upload
          const failed = newFiles.find((res: any) => res.code !== 200);
          if (failed) {
            return [obj, false, {
              dataKey: currentFile,
              message: convertToCamelCase(currentFile) + " : " + (failed.message || "Upload failed"),
              status: failed.code
            }];
          }

          uploadedFiles[key] = [
            ...filesPreviouslyUploaded,
            ...newFiles.map(({ code, ...rest }: any) => rest)
          ];
        }
        // ✅ Array of regular objects (like customer) - process recursively
        else if (value.every((item) => typeof item === "object" && item !== null)) {
          const results: any[] = [];

          for (const item of value) {
            const [processedItem, success, error] = await checkAnyFileToBeUpload(
              item,
              documentType
            );
            if (!success) {
              return [obj, false, error];
            }
            results.push(processedItem);
          }

          uploadedFiles[key] = results;
        }
        // ✅ Array of primitives - keep as is
        else {
          uploadedFiles[key] = value;
        }
      }
      else if (typeof value === "object" && value !== null) {
        if (value instanceof File) {
          const res = await uploadFileToServer(
            value,
            typeof documentType === "object" ? documentType[key] : documentType
          );
          if (res.code !== 200) {
            return [obj, false, {
              dataKey: currentFile,
              message: convertToCamelCase(currentFile) + " : " + (res.message || "Upload failed"),
              status: res.code
            }];
          }
          const { code, ...rest } = res;
          uploadedFiles[key] = rest;
        } else {
          const [processedSubObject, success, error] = await checkAnyFileToBeUpload(
            value,
            documentType
          );
          if (!success) {
            return [obj, false, error];
          }
          uploadedFiles[key] = processedSubObject;
        }
      }
      else if (value instanceof File) {
        const res = await uploadFileToServer(
          value,
          typeof documentType === "object" ? documentType[key] : documentType
        );
        if (res.code !== 200) {
          return [obj, false, {
            dataKey: currentFile,
            message: convertToCamelCase(currentFile) + " : " + (res.message || "Upload failed"),
            status: res.code
          }];
        }
        const { code, ...rest } = res;
        uploadedFiles[key] = rest;
      }
      else {
        uploadedFiles[key] = value;
      }
    }

    return [uploadedFiles, true];

  } catch (error) {
    let err = error as Error | AxiosError | ApiError;
    const status = (err as any).status;
    console.log("error in upload", { error });

    if (axios.isAxiosError(err)) {
      return [obj, false, {
        dataKey: currentFile,
        message: convertToCamelCase(currentFile) + " : " + err.response?.data?.message,
        status
      }];
    } else if (err instanceof ApiError) {
      return [obj, false, {
        dataKey: currentFile,
        message: convertToCamelCase(currentFile) + " : " + err.message,
        status
      }];
    }

    return [obj, false, {
      message: convertToCamelCase(currentFile) + " : " + err?.message,
      dataKey: currentFile,
      status
    }];
  }
};

export function createResponseContext<T>(initialState: QueryResponseContextProps<T>) {
  return createContext(initialState);
}

export function collectRoutes(routes: RouteConfig[], parentPath = ''): string[] {
  return routes.flatMap(route => {
    const fullPath = parentPath + (route.path.includes("/") ? route.path : "/" + route.path);
    if (route.children && route.children.length > 0) {
      return [fullPath, ...collectRoutes(route.children, fullPath)];
    }
    return [fullPath];
  });
}


export const VALIDATE_FORM = async (form: UseFormReturn, schema: ZodObject,) => {
  const fieldsToValidate = schema.keyof().options;
  form.clearErrors(fieldsToValidate);
  const result = await schema.safeParseAsync(form.getValues());
  if (!result.success) {
    result.error.issues.forEach(issue => form.setError(issue.path[0] as any, { type: 'manual', message: issue.message }));
    return false;
  }
  return true
}

/**
 * A higher-order function that wraps an API call with robust try/catch error handling.
 * It handles Axios errors, network errors, and validates that data is not empty.
 *
 * @param apiCall The "happy path" function that makes the actual API request.
 *                It should return the full Axios response.
 * @returns An async function that can be used directly as a `queryFn` in React Query.
 */
export const createApiRequestHandler = <TData>(
  apiCall: (...args: any[]) => Promise<AxiosResponse<TData>>
) => {
  // This is the new function that React Query will execute
  return async (...args: any[]): Promise<TData> => {
    try {
      // 1. Execute the provided API call function
      const response = await apiCall(...args);

      // 2. Validate the response data
      // If the API returns 200 OK but the data is null/undefined, treat it as an error.
      if (response && response.data) {
        return response.data;
      } else {
        // This is a "data issue". Throw an error to trigger React Query's error state.
        throw new Error("Invalid data format received from the server.");
      }
    } catch (error) {
      // 3. Handle all errors in one place
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const serverMessage = error.response?.data?.message;

        console.error(`API Error: Status ${status}`, error.response?.data || error.message);

        // Customize messages based on status code
        if (status === 404) {
          throw new Error("The requested resource was not found.");
        }
        if (status === 401) {
          throw new Error("You are not authorized to perform this action.");
        }
        if (status && status >= 500) {
          throw new Error("A server error occurred. Please try again later.");
        }

        // Use the server's error message as a fallback
        throw new Error(serverMessage || "An unknown error occurred with the request.");
      }

      // Re-throw our custom "Invalid data" error or any other unexpected errors
      throw error;
    }
  };
};